import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import cx from 'classnames';
import styles from '../stylesheets/main.module.css';
import Sidebar from './Sidebar.jsx';
import Todo from './Todo.jsx';
import storage from './storage.js';
import { getAppSurface, track, trackButtonClick, trackOncePerPage } from './analytics.js';
import MoonIcon from '../../assets/moon-icon.svg';
import MoonIconFilled from '../../assets/moon-icon-filled.svg';

const THEMES = ['violet', 'rose', 'teal', 'black'];
const DESKTOP_URL = 'https://focustodo.io';
const DESKTOP_APP_CTA_ENABLED = true;
const FEATURE_REQUEST_URL = 'mailto:jeremyukim@gmail.com?subject=FocusTodo%20Feature%20Request';
const DESKTOP_WINDOW_BACKGROUND_LIGHT = '#ffffff';
const DESKTOP_WINDOW_BACKGROUND_DARK_BY_THEME = {
  violet: '#181825',
  rose: '#25191f',
  teal: '#111715',
  black: '#0f0f0f',
};
const PWA_THEME_COLOR_LIGHT_BY_THEME = {
  violet: '#f3f2ff',
  rose: '#fff1f5',
  teal: '#edf8f7',
  black: '#f3f3f3',
};
const CHANGELOG_GROUPS = [
  {
    id: '2026-04-19',
    date: 'April 19, 2026',
    items: [
      {
        id: 'quick-add-shortcut',
        title: '⌘K quick capture and input polish',
        text: 'Use Command+K to jump into the todo field, press Escape to exit it, and enjoy a cleaner refreshed input with inline shortcut hints.',
      },
    ],
  },
  {
    id: '2026-02-23',
    date: 'February 23, 2026',
    items: [
      {
        id: 'desktop-app-available',
        title: '🖥️ Desktop app is now available',
        text: 'Get the app to quickly access it without your browser.',
      },
    ],
  },
  {
    id: '2026-02-20',
    date: 'February 20, 2026',
    items: [
      {
        id: 'timestamps',
        title: '⏱ Task timestamps',
        text: 'Todos now show when they were created or completed.',
      },
      {
        id: 'links',
        title: '🔗 Links in tasks and notes',
        text: 'Links are now automatically detected and made clickable.',
      },
      {
        id: 'code-highlighting',
        title: '⭐️ Syntax highlighting in code blocks',
        text: 'Multiline code blocks detect language patterns and render like an IDE.',
      },
      {
        id: 'markdown-cheatsheet',
        title: '📖 Markdown cheatsheet',
        text: 'A compact markdown reference is now available for notes.',
      },
    ],
  },
  {
    id: '2026-02-15',
    date: 'February 15, 2026',
    items: [
      {
        id: 'themes',
        title: '🎨 New colour themes',
        text: 'Customize the app with colour themes and full dark mode support for each.',
      },
      {
        id: 'markdown-notes',
        title: '📝 Markdown notes',
        text: 'Switch between notes or a pomodoro timer.',
      },
    ],
  },
];

function getGreetingMessage() {
  const hours = new Date().getHours();

  if (hours > 3 && hours < 12) return 'Good morning, ';
  if (hours >= 12 && hours < 18) return 'Good afternoon, ';
  return 'Good evening, ';
}

function readStoredBoolean(key, fallback) {
  try {
    const value = window.localStorage.getItem(key);
    if (value === 'true') return true;
    if (value === 'false') return false;
  } catch (error) {
    // Ignore storage read errors and fall back to defaults.
  }

  return fallback;
}

function readStoredTheme(fallback) {
  try {
    const value = window.localStorage.getItem('theme');
    if (THEMES.includes(value)) return value;
  } catch (error) {
    // Ignore storage read errors and fall back to defaults.
  }

  return fallback;
}

function isMacDesktopDevice() {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || '';
  const platform =
    (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '';
  const normalizedPlatform = platform.toLowerCase();
  const normalizedUserAgent = userAgent.toLowerCase();
  const isIPadOnDesktopUserAgent =
    normalizedPlatform.includes('mac') &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;

  if (isIPadOnDesktopUserAgent) return false;

  return normalizedPlatform.includes('mac') || normalizedUserAgent.includes('macintosh');
}

function getDesktopWindowBackgroundColor(theme, darkMode) {
  if (!darkMode) return DESKTOP_WINDOW_BACKGROUND_LIGHT;
  return DESKTOP_WINDOW_BACKGROUND_DARK_BY_THEME[theme] || DESKTOP_WINDOW_BACKGROUND_DARK_BY_THEME.violet;
}

function getPwaThemeColor(theme, darkMode) {
  if (darkMode) {
    return DESKTOP_WINDOW_BACKGROUND_DARK_BY_THEME[theme] || DESKTOP_WINDOW_BACKGROUND_DARK_BY_THEME.violet;
  }
  return PWA_THEME_COLOR_LIGHT_BY_THEME[theme] || PWA_THEME_COLOR_LIGHT_BY_THEME.violet;
}

function setThemeColorMeta(color) {
  if (typeof document === 'undefined') return;

  let themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.setAttribute('name', 'theme-color');
    document.head.appendChild(themeColorMeta);
  }

  themeColorMeta.setAttribute('content', color);
}

function isInstalledPwaDisplayMode() {
  if (typeof window === 'undefined') return false;

  const navigatorStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;
  const standaloneDisplayMode =
    typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
  const overlayDisplayMode =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: window-controls-overlay)').matches;

  return navigatorStandalone || standaloneDisplayMode || overlayDisplayMode;
}

export default function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readStoredBoolean('sidebarCollapsed', false),
  );
  const [darkMode, setDarkMode] = useState(() => readStoredBoolean('darkMode', false));
  const [theme, setTheme] = useState(() => readStoredTheme('violet'));
  const [highestId, setHighestId] = useState(0);
  const [message, setMessage] = useState(getGreetingMessage());
  const [appearanceHydrated, setAppearanceHydrated] = useState(false);
  const [mobileCtaMessage, setMobileCtaMessage] = useState('');
  const [canShareDesktopLink, setCanShareDesktopLink] = useState(false);
  const [deferredInstallPromptEvent, setDeferredInstallPromptEvent] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.__focusTodoDeferredInstallPrompt || null;
  });
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [isPwaInstalled, setIsPwaInstalled] = useState(() => isInstalledPwaDisplayMode());
  const isDesktopShell = typeof navigator !== 'undefined' && navigator.userAgent.includes('Electron');
  const canInstallDesktopApp = isMacDesktopDevice();
  const canPromptPwaInstall = deferredInstallPromptEvent !== null;
  const shouldShowDesktopAppButton =
    DESKTOP_APP_CTA_ENABLED &&
    !isDesktopShell &&
    canInstallDesktopApp &&
    !isPwaInstalled &&
    canPromptPwaInstall;

  useEffect(() => {
    const interval = window.setInterval(() => {
      setMessage(getGreetingMessage());
    }, 60000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    trackOncePerPage('app_open', {
      app_surface: getAppSurface(),
    });
  }, []);

  useEffect(() => {
    if (!appearanceHydrated) return;

    trackOncePerPage('appearance_settings_seen', {
      app_surface: getAppSurface(),
      theme_name: theme,
      dark_mode_enabled: darkMode ? 'true' : 'false',
    });
  }, [appearanceHydrated, darkMode, theme]);

  useLayoutEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    THEMES.forEach((themeName) => document.body.classList.remove(`theme-${themeName}`));
    document.body.classList.add(`theme-${theme}`);
  }, [theme, darkMode]);

  useEffect(() => {
    setThemeColorMeta(getPwaThemeColor(theme, darkMode));
  }, [theme, darkMode]);

  useEffect(() => {
    if (!isDesktopShell) return;

    const desktopApi = window.focusTodoDesktop;
    if (!desktopApi || typeof desktopApi.setWindowBackgroundColor !== 'function') return;

    desktopApi.setWindowBackgroundColor(getDesktopWindowBackgroundColor(theme, darkMode));
  }, [darkMode, isDesktopShell, theme]);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    setCanShareDesktopLink(typeof navigator.share === 'function');
  }, []);

  useEffect(() => {
    const syncDeferredInstallPromptEvent = () => {
      setDeferredInstallPromptEvent(window.__focusTodoDeferredInstallPrompt || null);
    };

    window.addEventListener('focustodo:installpromptready', syncDeferredInstallPromptEvent);
    syncDeferredInstallPromptEvent();

    return () => {
      window.removeEventListener('focustodo:installpromptready', syncDeferredInstallPromptEvent);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncInstalledState = () => {
      setIsPwaInstalled(isInstalledPwaDisplayMode());
    };
    const handleAppInstalled = () => {
      window.__focusTodoDeferredInstallPrompt = null;
      setDeferredInstallPromptEvent(null);
      track('pwa_installed', { app_surface: 'pwa' });
      syncInstalledState();
    };
    const standaloneMedia =
      typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : null;
    const overlayMedia =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: window-controls-overlay)')
        : null;

    window.addEventListener('appinstalled', handleAppInstalled);
    standaloneMedia?.addEventListener?.('change', syncInstalledState);
    overlayMedia?.addEventListener?.('change', syncInstalledState);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
      standaloneMedia?.removeEventListener?.('change', syncInstalledState);
      overlayMedia?.removeEventListener?.('change', syncInstalledState);
    };
  }, []);

  useEffect(() => {
    if (!isChangelogOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsChangelogOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isChangelogOpen]);

  useEffect(() => {
    let isMounted = true;

    storage
      .loadState()
      .then((result) => {
        if (!isMounted) return;

        const settings = result.state.settings || {};
        const loadedTheme = THEMES.includes(settings.theme) ? settings.theme : 'violet';

        setTheme(loadedTheme);
        setDarkMode(!!settings.darkMode);
        setSidebarCollapsed(!!settings.sidebarCollapsed);
        setHighestId(result.state.highestId || 0);
      })
      .catch(() => {
        // Keep defaults if persistence fails.
      })
      .finally(() => {
        if (!isMounted) return;
        setAppearanceHydrated(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((previous) => {
      const next = !previous;
      storage.saveSettings({ darkMode: next });
      track('appearance_settings_changed', {
        app_surface: getAppSurface(),
        change_type: 'dark_mode',
        theme_name: theme,
        dark_mode_enabled: next ? 'true' : 'false',
      });
      return next;
    });
  }, [theme]);

  const updateTheme = useCallback((nextTheme) => {
    if (!THEMES.includes(nextTheme)) return;

    setTheme((current) => {
      if (current === nextTheme) return current;
      storage.saveSettings({ theme: nextTheme });
      track('appearance_settings_changed', {
        app_surface: getAppSurface(),
        change_type: 'theme',
        theme_name: nextTheme,
        dark_mode_enabled: darkMode ? 'true' : 'false',
      });
      return nextTheme;
    });
  }, [darkMode]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((previous) => {
      const next = !previous;
      storage.saveSettings({ sidebarCollapsed: next });
      return next;
    });
  }, []);

  const handleDesktopLinkAction = useCallback(async () => {
    try {
      if (canShareDesktopLink && typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share({
          title: 'FocusTodo',
          text: 'Open FocusTodo on desktop for the full experience.',
          url: DESKTOP_URL,
        });
        setMobileCtaMessage('Link shared.');
        return;
      }

      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(DESKTOP_URL);
        setMobileCtaMessage('Desktop link copied.');
        return;
      }

      setMobileCtaMessage('Open focustodo.io on your desktop browser.');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      setMobileCtaMessage('Could not share right now. Open focustodo.io on desktop.');
    }
  }, [canShareDesktopLink]);

  const handleDesktopAppInstall = useCallback(async () => {
    trackButtonClick('desktop_app_cta', {
      app_surface: getAppSurface(),
      location: 'sidebar',
      prompt_available: deferredInstallPromptEvent ? 'true' : 'false',
    });

    if (!deferredInstallPromptEvent) return;

    try {
      track('pwa_install_prompt_shown', {
        app_surface: getAppSurface(),
        source: 'desktop_app_cta',
      });
      await deferredInstallPromptEvent.prompt();
      if (deferredInstallPromptEvent.userChoice) {
        try {
          const choice = await deferredInstallPromptEvent.userChoice;
          track('pwa_install_prompt_outcome', {
            app_surface: getAppSurface(),
            source: 'desktop_app_cta',
            outcome: choice?.outcome || 'unknown',
          });
          if (choice?.outcome === 'accepted') {
            setIsPwaInstalled(true);
          }
        } catch (error) {
          // Ignore prompt outcome errors.
        }
      }
    } catch (error) {
      // Ignore prompt errors.
    } finally {
      window.__focusTodoDeferredInstallPrompt = null;
      setDeferredInstallPromptEvent(null);
    }
  }, [deferredInstallPromptEvent]);

  return (
    <div
      className={cx(
        styles.fullPage,
        !appearanceHydrated ? styles.deferTransitions : null,
      )}
    >
      <div className={styles.appLayout}>
        <div
          className={cx(
            styles.sidebarColumn,
            sidebarCollapsed ? styles.sidebarCollapsed : null,
          )}
        >
          <div className={styles.sidebarInner}>
            <Sidebar darkMode={darkMode} highestId={highestId} />
            <div className={styles.todoPlaceholder}>
              <div className={styles.todoPlaceholderHeading}>What needs to be done?</div>
              <div className={styles.noActivity}>
                <div className={styles.noActivityLightbulb}>💡</div>
                <span>Add to-dos on the desktop site</span>
              </div>
            </div>
          </div>

          <div className={styles.sidebarLinkContainer}>
            <div className={styles.sidebarLinks}>
              <div
                className={cx(
                  styles.appearanceControls,
                  !shouldShowDesktopAppButton && styles.appearanceControlsWithoutAppButton,
                )}
              >
                <button
                  type="button"
                  className={styles.darkModeToggle}
                  onClick={toggleDarkMode}
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  <img className={styles.moonIcon} src={darkMode ? MoonIconFilled : MoonIcon} alt="" />
                  <div className={cx(styles.toggleTrack, darkMode && styles.toggleTrackActive)}>
                    <div className={styles.toggleThumb}></div>
                  </div>
                </button>

                <div className={styles.themePicker} role="group" aria-label="Color theme">
                  <button
                    type="button"
                    className={cx(styles.themeSwatchButton, theme === 'violet' && styles.themeSwatchActive)}
                    onClick={() => updateTheme('violet')}
                    aria-label="Switch to violet theme"
                  >
                    <span className={cx(styles.themeSwatch, styles.themeSwatchViolet)}></span>
                  </button>
                  <button
                    type="button"
                    className={cx(styles.themeSwatchButton, theme === 'rose' && styles.themeSwatchActive)}
                    onClick={() => updateTheme('rose')}
                    aria-label="Switch to rose theme"
                  >
                    <span className={cx(styles.themeSwatch, styles.themeSwatchRose)}></span>
                  </button>
                  <button
                    type="button"
                    className={cx(styles.themeSwatchButton, theme === 'teal' && styles.themeSwatchActive)}
                    onClick={() => updateTheme('teal')}
                    aria-label="Switch to teal theme"
                  >
                    <span className={cx(styles.themeSwatch, styles.themeSwatchTeal)}></span>
                  </button>
                  <button
                    type="button"
                    className={cx(styles.themeSwatchButton, theme === 'black' && styles.themeSwatchActive)}
                    onClick={() => updateTheme('black')}
                    aria-label="Switch to black theme"
                  >
                    <span className={cx(styles.themeSwatch, styles.themeSwatchBlack)}></span>
                  </button>
                </div>
              </div>

              {shouldShowDesktopAppButton ? (
                <button
                  type="button"
                  className={styles.desktopAppButton}
                  onClick={handleDesktopAppInstall}
                >
                  Get the app
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div
          className={cx(
            styles.todo,
          )}
        >
          <div className={styles.todoInner}>
            <Todo message={message} onHighestIdChange={setHighestId} />
          </div>
        </div>

        <button
          type="button"
          className={cx(styles.toggleSidebar, sidebarCollapsed && styles.toggleSidebarCollapsed)}
          onClick={(event) => {
            event.currentTarget.blur();
            toggleSidebar();
          }}
        >
          {sidebarCollapsed ? '›' : '‹'}
        </button>

        <button
          type="button"
          className={styles.infoFab}
          onClick={() => setIsChangelogOpen(true)}
          aria-label="Open changelog and feature request modal"
        >
          <svg className={styles.infoFabIcon} viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </button>
      </div>

      {isChangelogOpen ? (
        <div
          className={styles.changelogModalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="changelogTitle"
          onClick={() => setIsChangelogOpen(false)}
        >
          <div
            className={styles.changelogModalCard}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.changelogCloseButton}
              onClick={() => setIsChangelogOpen(false)}
              aria-label="Close changelog"
            >
              ×
            </button>

            <h2 id="changelogTitle" className={styles.changelogTitle}>What&apos;s new</h2>

            <div className={styles.changelogList}>
              {CHANGELOG_GROUPS.map((group) => (
                <div key={group.id} className={styles.changelogGroup}>
                  <div className={styles.changelogDate}>{group.date}</div>
                  <div className={styles.changelogGroupCard}>
                    {group.items.map((item) => (
                      <div key={item.id} className={styles.changelogItem}>
                        <div className={styles.changelogItemTitle}>{item.title}</div>
                        <div className={styles.changelogItemText}>{item.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <a className={styles.changelogPrimaryAction} href={FEATURE_REQUEST_URL}>
              Submit feature request
            </a>
          </div>
        </div>
      ) : null}

      <div
        className={styles.mobileDesktopModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobileDesktopTitle"
      >
        <div className={styles.mobileDesktopCard}>
          <h2 id="mobileDesktopTitle" className={styles.mobileDesktopTitle}>
            FocusTodo is built for laptop and desktop
          </h2>

          <div className={styles.mobileDesktopFeatureGrid}>
            <div className={styles.mobileDesktopFeatureItem}>✅ Write notes with full markdown support</div>
            <div className={styles.mobileDesktopFeatureItem}>✅ Keep track of your time with a pomodoro timer</div>
            <div className={styles.mobileDesktopFeatureItem}>✅ Start using instantly, no account required</div>
          </div>

          <p className={styles.mobileDesktopStatus}>
            {mobileCtaMessage || 'Send the link to your computer and open it there.'}
          </p>

          <div className={styles.mobileDesktopActions}>
            <button
              type="button"
              className={styles.mobileDesktopPrimaryAction}
              onClick={handleDesktopLinkAction}
            >
              Share link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
